import { useEffect, useState } from 'react';
import { CalendarDay } from 'further-ui/utils';
import { Controller } from 'react-hook-form';
import GridContainer from 'components/GridContainer';
import { Button, Typography, Grid, Alert, styled } from '@mui/material';
import PageLoader from 'components/PageLoader';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import ManagerOverViewForm from './ManagerOverViewForm';
import ClosedFundsList from './ClosedFundsList';
import useStyles from './styles';
import useInvestorReportingForm, {
  fundInformationSchema,
} from 'hooks/form/useInvestorReportingForm';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { status } from 'adminConstants/investorReporting';
import { useInvestorReportingFormContext } from 'contexts/InvestorReportingFormContext';
import ReportingDraftButtons from 'components/ReportingDraftButtons';
import Heading from 'components/Heading';
import useExportReportingSample from 'hooks/data/investorReporting/useExportReportingSample';

const StyledAlert = styled(Alert)(({ theme }) => ({
  '& .MuiAlert-message': {
    paddingTop: theme.spacing(2),
  },
}));

const FundInformationStep = ({
  firm,
  isFetching,
  isViewPage,
  hasActiveFundsToReportOn,
  onNextStep,
  onExitForm,
}) => {
  const { classes, cx } = useStyles();
  const [isDirty, setIsDirty] = useState(false);
  const { submitForm, scrollToFirstError, isSubmitting } =
    useInvestorReportingForm();

  const formContext = useInvestorReportingFormContext();
  const {
    submitDate,
    setSubmitDate,
    // @ts-expect-error
    processFundInformation,
    investorReport,
    reportStatus,
    fundInformation,
    fundTypes,
    isFetchingFundTypes,
    selectedFundTypes,
    setSelectedFundTypes,
  } = formContext;
  const isCreatingInvestorReport = !investorReport?._id;
  const isUpdatingInvestorReport = !!investorReport;
  const isInvestorReportWithFundTypes = investorReport?.fundTypes;
  const noSelectedFundTypes = selectedFundTypes?.length === 0;

  const { handleSubmit, control, setValue, getValues, watch } = useForm({
    shouldFocusError: true,
    resolver: zodResolver(fundInformationSchema),
    criteriaMode: 'all',
  });

  const { exportSample, isPending } = useExportReportingSample();

  useEffect(() => {
    // reset scroll state back to the top
    window.scrollTo(0, 0);
    const { unsubscribe } = watch(() => {
      setIsDirty(true);
    });

    return unsubscribe;
  }, [watch]);

  useEffect(() => {
    setValue('fundInformation', fundInformation);
  }, [fundInformation]);

  const onValidationError = (errors) => {
    scrollToFirstError(errors);
  };

  const onQuickSave = async (inputData) => {
    const data = inputData ?? getValues();

    const submissionData = {
      ...formContext,
      managerOverview: {
        ...(formContext.managerOverview ?? {}),
        ...data.managerOverview,
      },
      fundInformation: processFundInformation(data.fundInformation),
    };

    if (
      data.managerOverview?.managerOverViewTitle &&
      data.managerOverview?.managerOverViewShortDescription
    ) {
      await submitForm(submissionData, reportStatus ?? status.DRAFT, true);
      setIsDirty(false);
    }
  };

  const quickSaveBeforeNextStep = async (data) => {
    const quickSaveDisabled = reportStatus === status.SUBMITTED || isViewPage;
    if (!quickSaveDisabled) {
      await onQuickSave(data);
    }
    onNextStep(data);
  };

  const handleDownloadSample = () => {
    if (!isPending && investorReport?._id) {
      exportSample(investorReport._id);
    }
  };

  if (!firm) return null;

  return (
    <form onSubmit={handleSubmit(quickSaveBeforeNextStep, onValidationError)}>
      <GridContainer>
        <Grid
          className={classes.dateInput}
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Heading variant="h2">Enter date of report</Heading>
          <Controller
            name="submitDate"
            defaultValue={submitDate}
            control={control}
            render={({ formState: { errors } }) => (
              <AppDatePicker
                id="submitDate"
                required
                name="submitDate"
                value={submitDate.toLocalDate()}
                onChange={(date) => {
                  const calendarDay = new CalendarDay(date ?? undefined);
                  setSubmitDate(calendarDay);
                  setValue('submitDate', calendarDay.toLocalDate());
                }}
                error={!!errors?.submitDate}
                helperText={errors?.submitDate?.message?.toString()}
                disabled={isViewPage}
              />
            )}
          />
        </Grid>
      </GridContainer>

      {(isCreatingInvestorReport || isInvestorReportWithFundTypes) && (
        <GridContainer>
          <Grid
            className={classes.dateInput}
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <Heading variant="h2">Filter update</Heading>
            {fundTypes?.map((fundType) => (
              <AppCheckbox
                label={fundType.name}
                key={fundType._id}
                checked={selectedFundTypes?.includes(fundType._id)}
                onChange={(event) => {
                  setSelectedFundTypes(
                    event.target.checked
                      ? [...(selectedFundTypes ?? []), fundType._id]
                      : selectedFundTypes?.filter((id) => id !== fundType._id),
                  );
                }}
                disabled={isViewPage || isUpdatingInvestorReport || isFetching}
              />
            ))}
          </Grid>
        </GridContainer>
      )}

      {isFetching || isFetchingFundTypes ? (
        <PageLoader />
      ) : (
        <>
          <Heading variant="h2">Enter fund information</Heading>
          {hasActiveFundsToReportOn ? (
            <>
              <ManagerOverViewForm
                classes={classes}
                disabled={isViewPage}
                control={control}
                setValue={setValue}
                summaryClassName={cx(
                  classes.cmAccHeader,
                  classes.expends,
                  classes.fundsAccHeader,
                )}
              />
              <ClosedFundsList
                firmName={firm?.firmName}
                isViewPage={isViewPage}
                control={control}
                setValue={setValue}
              />
            </>
          ) : (
            <StyledAlert severity="info">
              {noSelectedFundTypes ? (
                <Typography className={classes.noTranchesInfoText}>
                  Please choose at least one fund type from the list above
                </Typography>
              ) : (
                <>
                  <Typography className={classes.noTranchesInfoText}>
                    No tranches require a report on this date. There are either:
                  </Typography>
                  <ul className={classes.noTranchesInfoList}>
                    <li>No closed tranches on the selected date, or;</li>
                    <li>
                      The closed tranches contain no active subscriptions on
                      this date, or;
                    </li>
                    <li>
                      The closed tranches only contain companies that have been
                      fully exited on this date.
                    </li>
                  </ul>
                </>
              )}
            </StyledAlert>
          )}
        </>
      )}

      <div className={classes.buttonContainer}>
        <div className={classes.buttonLeftContainer}>
          <Button variant="outlined" onClick={onExitForm}>
            Back
          </Button>
        </div>

        {!!hasActiveFundsToReportOn && (
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting
              ? 'Saving...'
              : 'Proceed to portfolio company updates'}
          </Button>
        )}
      </div>
      <ReportingDraftButtons
        // @ts-expect-error
        onQuickSave={onQuickSave}
        isQuickSaving={isSubmitting}
        onDownloadSample={handleDownloadSample}
        isDownloading={isPending}
        allowDownload={!!investorReport?._id && !isDirty}
        showDownloadButton={true}
        disabled={reportStatus === status.SUBMITTED || isViewPage}
      />
    </form>
  );
};

export default FundInformationStep;
